import React, { useState, useEffect } from 'react';
import { trackPageView } from '../utils/analytics';
import { trackGAEvent, trackPixelCheckout } from '../utils/googleAnalytics';
import { useNavigate, Link } from 'react-router-dom';
import Footer from './shared/Footer';
import config, { PRICING } from '../config';
import { Box, Container, Typography, Grid, Card, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup, Chip, Modal, IconButton, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DiscountIcon from '@mui/icons-material/Discount';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import NavBar from './shared/NavBar';
import ChristmasOfferBanner from './shared/ChristmasOfferBanner';
import OfferBanner from './shared/OfferBanner';
import PaymentSuccess from './PaymentSuccess';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchWithAuth } from '../utils/apiAuth';
import MagazineCoverGrid from './shared/MagazineCoverGrid';
import EditionsInfo from './shared/EditionsInfo';
import Testimonials from './shared/Testimonials';
import FAQ from './shared/FAQ';

function loadScript(src) {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(script);
  });
}

const PlanCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(4),
  textAlign: 'center',
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 16px 40px rgba(255, 107, 53, 0.2)',
    background: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 107, 53, 0.3)'
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  color: 'rgba(255, 255, 255, 0.9)',
  '& svg': {
    color: '#FF6B35',
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
    filter: 'drop-shadow(0 0 4px rgba(255, 107, 53, 0.3))',
    marginTop: '2px'
  },
  '& .MuiTypography-root': {
    lineHeight: 1.4,
    fontSize: { xs: '0.85rem', sm: '0.95rem' },
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    flex: 1
  }
}));

const PricingContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.02)',
    backdropFilter: 'blur(5px)',
    borderRadius: '20px',
    zIndex: -1
  }
}));

const PlanFeatures = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  textAlign: 'left',
  width: '100%'
}));

const PlanPrice = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 'bold',
  background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(1),
  textShadow: '0 0 20px rgba(255, 107, 53, 0.3)'
}));

const TotalPrice = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: 'rgba(255, 255, 255, 0.7)',
  marginBottom: theme.spacing(2),
}));

// Full-screen image modal components with glass effect
const ImageModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(10px)',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: '95vw',
  maxHeight: '95vh',
  background: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '20px',
  backdropFilter: 'blur(15px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
  padding: theme.spacing(2),
  animation: 'fadeInUp 0.4s ease-out',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(30px) scale(0.9)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)'
    }
  }
}));

const ModalImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  maxHeight: '85vh',
  objectFit: 'contain',
  borderRadius: '16px',
  boxShadow: '0 15px 35px rgba(0, 0, 0, 0.3)',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  color: 'white',
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgba(255, 107, 53, 0.8)',
    transform: 'scale(1.1)',
  },
  transition: 'all 0.3s ease'
}));

const PaymentDialog = ({ open, onClose, plan, formData, handleInputChange, handleSubmit, couponStatus, validateCoupon, finalTotalAmount, isCreatingSubscription, isValidatingCoupon, setCouponStatus, setFinalTotalAmount }) => {
  const [errors, setErrors] = useState({});
  if (!plan) return null;

  const totalAmount = plan.price * plan.totalMonths;
  const monthlyAmount = totalAmount / plan.totalMonths;
  const finalMonthlyAmount = finalTotalAmount / plan.totalMonths;

  const handleRemoveCoupon = () => {
    handleInputChange({ target: { name: 'couponCode', value: '' } });
    setCouponStatus({
      isValid: false,
      message: '',
      discount: null
    });
    setFinalTotalAmount(totalAmount);
  };

  const handleInterestChange = (interest, checked) => {
    handleInputChange({
      target: {
        name: 'interests',
        value: {
          ...formData.interests,
          [interest]: checked
        }
      }
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ color: '#2D3748', fontWeight: 'bold', textAlign: 'center' }}>
          {plan.name}
          {isCreatingSubscription && (
            <CircularProgress 
              size={24} 
              style={{ 
                marginLeft: '10px',
                verticalAlign: 'middle'
              }} 
            />
          )}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, px: { xs: 2, sm: 3 } }}>
        <Box component="form" onSubmit={(e) => {
          e.preventDefault();
          const newErrors = {};
          if (!formData.name) newErrors.name = 'Name is required';
          if (!formData.email) newErrors.email = 'Email is required';
          if (!formData.phone) newErrors.phone = 'Phone number is required';
          if (!formData.childAge) newErrors.childAge = 'Child\'s age is required';
          if (!formData.childGender) newErrors.childGender = 'Please select child\'s gender';

          if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
          }
          setErrors({});
          handleSubmit(e);
        }} sx={{ mt: 0 }}>

          <TextField
            margin="dense"
            label="Full Name"
            type="text"
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
            size="small"
            sx={{ mb: 1.2 }}
            disabled={isCreatingSubscription}
          />
          <TextField
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            size="small"
            error={!!errors.email}
            helperText={errors.email}
            sx={{ mb: errors.email ? 0.5 : 1.2 }}
            disabled={isCreatingSubscription}
          />
          <TextField
            margin="dense"
            label="Phone Number"
            type="tel"
            fullWidth
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            required
            size="small"
            error={!!errors.phone}
            helperText={errors.phone}
            sx={{ mb: errors.phone ? 0.5 : 1.2 }}
            disabled={isCreatingSubscription}
          />
          <TextField
            margin="dense"
            label="Age of Child"
            type="number"
            fullWidth
            name="childAge"
            value={formData.childAge}
            onChange={handleInputChange}
            required
            size="small"
            error={!!errors.childAge}
            helperText={errors.childAge}
            sx={{ mb: errors.childAge ? 0.5 : 0.5 }}
            disabled={isCreatingSubscription}
          />
          {!errors.childAge && (
            <Box sx={{ 
              mb: 1.5, 
              p: 0.8, 
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.08) 0%, rgba(6, 174, 213, 0.08) 100%)',
              border: '1px solid rgba(255, 107, 53, 0.2)',
              textAlign: 'center'
            }}>
              <Typography sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.8rem' },
                fontWeight: 600,
                color: '#2D3748',
                letterSpacing: '0px',
                lineHeight: 1.3
              }}>
                📚 Beginner (1-5) • Junior (6-9) • Senior (10-15)
              </Typography>
            </Box>
          )}
          <FormControl component="fieldset" error={!!errors.childGender} sx={{ mb: errors.childGender ? 0.5 : 1.5 }} fullWidth>
            <FormLabel component="legend">Child's Gender</FormLabel>
            <RadioGroup
              row
              name="childGender"
              value={formData.childGender}
              onChange={handleInputChange}
            >
            {errors.childGender && (
              <FormHelperText error>{errors.childGender}</FormHelperText>
            )}
              <FormControlLabel value="boy" control={<Radio />} label="Boy" disabled={isCreatingSubscription} />
              <FormControlLabel value="girl" control={<Radio />} label="Girl" disabled={isCreatingSubscription} />
            </RadioGroup>
          </FormControl>
   
         
          <Box sx={{
                mt: 0,
                mb: 1.5,
                p: 1.5,
                borderRadius: 2,
                border: '2px solid',
                borderColor: couponStatus.isValid ? '#FF6B35' : '#FF6B35',
                background: couponStatus.isValid ? 'white' : 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}>
              {couponStatus.message && (
                <Typography 
                  sx={{
                    color: couponStatus.isValid ? '#2E7D32' : '#D32F2F',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                >
                  {couponStatus.message} {couponStatus.isValid ? '🎉' : ""} 
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {plan.period === 'yearly' ? (
                  // Yearly subscription display
                  couponStatus.isValid ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                     
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#666',
                          fontSize: { xs: '0.85rem', sm: '0.9rem' }
                        }}
                      >
                        ₹{Math.round(finalMonthlyAmount)}/month × 12 = ₹{Math.round(finalTotalAmount)}/year
                      </Typography> 

                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#666',
                          fontSize: { xs: '0.85rem', sm: '0.9rem' }
                        }}
                      >
                        Was: <span style={{ textDecoration: 'line-through' }}> ₹{plan.price * 12}/year</span>
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#2D3748',
                          fontWeight: 'bold',
                          fontSize: { xs: '1rem', sm: '1.1rem' }
                        }}
                      >
                        To Pay: ₹{Math.round(finalTotalAmount)}/year (For 50+ copies)
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#666',
                          fontSize: { xs: '0.85rem', sm: '0.9rem' }
                        }}
                      >
                        ₹{plan.price}/month × 12 = ₹{totalAmount}/year
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#2D3748',
                          fontWeight: 'bold',
                          fontSize: { xs: '1rem', sm: '1.1rem' }
                        }}
                      >
                        To Pay: ₹{totalAmount}/year (For 50+ copies)
                      </Typography>
                    </Box>
                  )
                ) : (
                  // Monthly subscription display
                  couponStatus.isValid ? (
                    <>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#666',
                          fontSize: { xs: '0.85rem', sm: '0.9rem' }
                        }}
                      >
                        Was: <span style={{ textDecoration: 'line-through' }}>₹{plan.price}/month</span>
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#2D3748',
                          fontWeight: 'bold',
                          fontSize: { xs: '1rem', sm: '1.1rem' }
                        }}
                      >
                       To Pay: ₹{Math.round(finalMonthlyAmount)}/month
                      </Typography>
                    </>
                  ) : (
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#2D3748',
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem', sm: '1.1rem' }
                      }}
                    >
                      To Pay: ₹{plan.price}/month
                    </Typography>
                  )
                )}
              
              </Box>
            
              
              {/* )} */}
            </Box>
          {/* )} */}
                 <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              margin="dense"
              label="Have a Coupon Code?"
              type="text"
              fullWidth
              name="couponCode"
              value={formData.couponCode}
              onChange={handleInputChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {!couponStatus.isValid ? (
                      <Button
                        onClick={validateCoupon}
                        disabled={!formData.couponCode || isValidatingCoupon || isCreatingSubscription}
                      >
                        {isValidatingCoupon ? (
                          <CircularProgress size={24} />
                        ) : (
                          'Apply'
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleRemoveCoupon}
                        disabled={isValidatingCoupon || isCreatingSubscription}
                        color="error"
                      >
                        Remove
                      </Button>
                    )}
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <DiscountIcon color={couponStatus.isValid ? "success" : "action"} />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
              disabled={isValidatingCoupon || isCreatingSubscription || couponStatus.isValid}
            />
          </Box>
          
          {/* Cancellation Policy Notice */}
          <Box sx={{
            mt: 0,
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(6, 174, 213, 0.1) 0%, rgba(6, 174, 213, 0.05) 100%)',
            border: '1px solid rgba(6, 174, 213, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{
              minWidth: '24px',
              height: '24px',
              borderRadius: '50%',
              background: '#06AED5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              ℹ
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#2D3748',
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                lineHeight: 1.4
              }}
            >
              <strong>Cancel Anytime</strong>—just email to <a href="mailto:info@youngpresso.com" style={{ color: '#06AED5', textDecoration: 'none', fontWeight: 'bold' }}>info@youngpresso.com</a>. No hassle, no questions asked.
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: '#FF6B35',
              '&:hover': {
                backgroundColor: '#e85f2f'
              }
            }}
            disabled={isCreatingSubscription}
          >
            {isCreatingSubscription ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Proceed to Payment'
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const PricingAndPlans = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    couponCode: '',
    childAge: '',
    childGender: '',
    interests: {}
  });
  const [couponStatus, setCouponStatus] = useState({
    isValid: false,
    message: '',
    discount: null
  });
  const [finalTotalAmount, setFinalTotalAmount] = useState(0);
  const [error, setError] = useState(null);
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: '', alt: '' });
  

  const apiBaseUrl = config.getApiUrl();

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Track page view for analytics (completely non-blocking)
  useEffect(() => {
    // Use requestIdleCallback for maximum non-blocking behavior
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        trackPageView('pricing');
      }, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        trackPageView('pricing');
      }, 1500);
    }
  }, []); // Empty dependency array means this runs once on mount

  const fetchWithApiKey = async (url, options = {}) => {
    const apiKey = process.env.REACT_APP_API_KEY;
    if (!apiKey) {
      throw new Error('API key not configured');
    }

    const headers = {
      'Content-Type': 'application/json',
      'X-API-Key': apiKey,
      ...options.headers
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'API request failed');
    }

    return response.json();
  };

  const handlePayment = async (subscriptionId) => {
    // First verify if the subscription exists and is ready for payment
    try {
      const subscriptionStatus = await fetchWithApiKey(
        `${apiBaseUrl}/api/subscriptions/${subscriptionId}/status`
      );
      
      if (!subscriptionStatus.ok) {
        throw new Error(subscriptionStatus.message || 'Unable to verify subscription status');
      }

      if (subscriptionStatus.subscription.status !== 'created') {
        throw new Error('Subscription is not in a valid state for payment');
      }
    } catch (error) {
      console.error('Error verifying subscription:', error);
      setError('Unable to initialize payment. Please try again.');
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      subscription_id: subscriptionId,
      name: "Youngpresso",
      description: `${selectedPlan.period === 'yearly' ? 'Yearly' : 'Monthly'} Subscription`,
      image: "/logo.png",
      handler: async function(response) {
        let verificationPending = false;

        try {
          const verificationResponse = await fetchWithApiKey(`${apiBaseUrl}/api/subscriptions/verify-payment`, {
            method: 'POST',
            body: JSON.stringify({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature
            })
          });

          if (!verificationResponse.success) {
            verificationPending = true;
            console.warn('Payment verification returned unsuccessful:', verificationResponse.message);
          }
        } catch (error) {
          verificationPending = true;
          console.error('Payment verification error (Razorpay payment succeeded):', error);
        }

        // Razorpay handler only fires after successful payment — always show success page
        if (window.RazorpayCheckout) {
          window.RazorpayCheckout.close();
        }

        setError(null);

        const originalAmount = selectedPlan.period === 'monthly' ? selectedPlan.price : (selectedPlan.price * selectedPlan.totalMonths);
        const actualPaidAmount = selectedPlan.period === 'monthly' ? (finalTotalAmount / selectedPlan.totalMonths) : finalTotalAmount;

        navigate('/payment-success', {
          state: {
            paymentId: response.razorpay_payment_id,
            subscriptionId: response.razorpay_subscription_id,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            plan: `${selectedPlan.period === 'yearly' ? 'Yearly' : 'Monthly'} Plan`,
            amount: originalAmount,
            amountPaid: actualPaidAmount,
            originalAmount: originalAmount,
            hasDiscount: couponStatus.isValid,
            couponCode: formData.couponCode || '',
            discountAmount: couponStatus.isValid ? (originalAmount - actualPaidAmount) : 0,
            verificationPending
          }
        });
      },
      modal: {
        ondismiss: async function() {
          // Handle payment modal dismissal
          try {
            await updateSubscriptionPayment(subscriptionId, {
              status: 'FAILED',
              failureReason: 'Payment modal dismissed by user'
            });
          } catch (error) {
            // Silently handle error - subscription update failure on dismissal is not critical
          }
          // Ensure any blank windows are closed
          if (window.RazorpayCheckout) {
            window.RazorpayCheckout.close();
          }
        },
        escape: false // Prevent closing on escape key
      },
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.phone
      },
      notes: {
        childAge: formData.childAge,
        childGender: formData.childGender,
        interests: JSON.stringify(formData.interests)
      },
      theme: {
        color: "#FF6B35"
      }
    };

    try {
      if (!window.Razorpay) {
        throw new Error('Razorpay SDK not loaded');
      }
      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      console.error('Error opening Razorpay:', error);
      setError('Unable to open payment window. Please try again.');
      await updateSubscriptionPayment(subscriptionId, {
        status: 'FAILED',
        failureReason: error.message || 'Failed to initialize payment'
      });
    }
  };

  const verifyPayment = async (paymentResponse) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/subscriptions/verify-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          razorpay_payment_id: paymentResponse.razorpay_payment_id,
          razorpay_subscription_id: paymentResponse.razorpay_subscription_id,
          razorpay_signature: paymentResponse.razorpay_signature
        })
      });

      const data = await response.json();
      if (response.ok) {
        setOpen(false);
      } else {
        setError('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setError('Payment verification failed. Please contact support.');
    }
  };

  const plans = [
    {
      id: PRICING.PLANS.MONTHLY.ID,
      name: 'Monthly Subscription',
      price: PRICING.PLANS.MONTHLY.BASE_PRICE,
      features: [
        { text: 'Digital issues delivered on WhatsApp', icon: 'whatsapp' },
        'Get Latest Issue every Saturday morning <b>(4 issues/month)</b>',
        'Filtered NEWS in Kids-Understandable Language',
        'Latest India NEWS',
        'Top International NEWS',
        'Exciting Sports NEWS',
        'Latest Technology NEWS',
       'Regular <b>Quizzes</b> and <b>Rewards</b> to motivate Kids',
        '<b>CANCEL-ANYTIME</b> Policy. No hassle, no questions asked.'
      ],
      period: PRICING.PLANS.MONTHLY.PERIOD,
      totalMonths: PRICING.PLANS.MONTHLY.TOTAL_MONTHS
    },
    {
      id: PRICING.PLANS.YEARLY.ID,
      name: 'Annual Subscription',
      price: PRICING.PLANS.YEARLY.BASE_PRICE,
      badge: 'Super Saver!',
      features: [
        'All Monthly Features',
        'Get Latest Issue every Saturday morning (54 issues/year)',
        '<b>SAVE RS. 600!</b>',
          '<b>CANCEL-ANYTIME </b> Policy. No hassle, no questions asked.'
      ],
      period: PRICING.PLANS.YEARLY.PERIOD,
      totalMonths: PRICING.PLANS.YEARLY.TOTAL_MONTHS
    }
  ];

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setFinalTotalAmount(plan.price * plan.totalMonths);
    setOpen(true);
    setError(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      couponCode: '',
      childAge: '',
      childGender: '',
      interests: {}
    });
    setCouponStatus({
      isValid: false,
      message: '',
      discount: null
    });

    // Track GTM event
    const eventName = plan.period === 'monthly' ? 'choose_monthly_plan' : 'choose_yearly_plan';
    trackGAEvent(eventName, {
      plan_id: plan.id,
      plan_name: plan.name,
      plan_period: plan.period,
      plan_price: plan.price
    });
  };

  const validateCoupon = async () => {
    if (!formData.couponCode || !selectedPlan) return;

    setIsValidatingCoupon(true);
    setError(null);

    try {
      const response = await fetch(`${config.getApiUrl()}/api/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          code: formData.couponCode,
          originalAmount: selectedPlan.price * selectedPlan.totalMonths
        })
      });

      const data = await response.json();
      
      if (response.ok && data.isValid) {
        setCouponStatus({
          isValid: true,
          message: data.message,
          discountType: data.discountType,
          discountValue: data.discountValue,
          discountedAmount: data.discountedAmount
        });
        setFinalTotalAmount(data.discountedAmount);

      } else {
        setCouponStatus({
          isValid: false,
          message: data.message || 'Failed to validate coupon'
        });
        setFinalTotalAmount(selectedPlan.price * selectedPlan.totalMonths);
        // Clear the coupon code field when validation fails
        setFormData(prev => ({
          ...prev,
          couponCode: ''
        }));
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponStatus({
        isValid: false,
        message: 'Failed to validate coupon. Please try again.'
      });
      setFinalTotalAmount(selectedPlan.price * selectedPlan.totalMonths);
      // Clear the coupon code field when validation fails due to an error
      setFormData(prev => ({
        ...prev,
        couponCode: ''
      }));
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Convert coupon code to uppercase automatically
    if (name === 'couponCode') {
      setFormData(prev => ({
        ...prev,
        [name]: value.toUpperCase()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPlan) {
      setError('Please select a plan first');
      return;
    }

    trackPixelCheckout({
      value: finalTotalAmount,
      planName: selectedPlan.name,
      planId: selectedPlan.id,
      planPeriod: selectedPlan.period
    });

    setIsCreatingSubscription(true);
    setError(null);

    try {
      const requestBody = {
        planId: selectedPlan.id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        couponCode: formData.couponCode || undefined,
        monthlyAmount: finalTotalAmount / selectedPlan.totalMonths,
        period: selectedPlan.period,
        childAge: formData.childAge,
        childGender: formData.childGender,
        interests: formData.interests
      };

      const data = await fetchWithAuth(`${apiBaseUrl}/api/subscriptions/create`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (data.error) {
        throw new Error(data.error);
      }

      // Handle successful subscription creation
      handlePayment(data.subscriptionId);
    } catch (error) {
      console.error('Error creating subscription:', error);
      setError(error.message || 'Failed to create subscription');
    } finally {
      setIsCreatingSubscription(false);
    }
  };

  const updateSubscriptionPayment = async (subscriptionId, paymentDetails) => {
    try {
      const data = await fetchWithApiKey(`${apiBaseUrl}/api/subscriptions/${subscriptionId}/update-payment`, {
        method: 'PATCH',
        body: JSON.stringify(paymentDetails)
      });

      if (!data.success) {
        throw new Error(data.message || 'Failed to update subscription payment');
      }

      return data;
    } catch (error) {
      console.error('Error updating subscription payment:', error);
      throw error; // Re-throw the error so it can be handled by the caller
    }
  };

  const handleImageClick = (imageUrl, imageAlt) => {
    setSelectedImage({ url: imageUrl, alt: imageAlt });
    setImageModalOpen(true);
  };

  const handleImageModalClose = () => {
    setImageModalOpen(false);
    setSelectedImage({ url: '', alt: '' });
  };

  const scrollToMonthlyPlan = () => {
    const monthlyPlanElement = document.querySelector('[data-plan-id="xx1"]');
    if (monthlyPlanElement) {
      monthlyPlanElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  };


  const renderPlanPrice = (plan, finalPrice = null) => {
    const price = finalPrice || plan.price;
    const totalMonths = plan.totalMonths || 12; // Default to 12 if totalMonths is not defined
    const yearlyTotal = price * totalMonths;
    
    return (
      <Box sx={{ textAlign: 'center' }}>
       
       {plan.period === 'yearly' && (
           <Typography variant="h5" sx={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#FF6B35', marginBottom: 1 }}>
           ₹{(price*12)}
           <Typography component="span" variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
             /year
           </Typography>
         </Typography>
        )}
   
   {plan.period === 'monthly' && (
          <Typography variant="h5" sx={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#FF6B35', marginBottom: 1 }}>
          ₹{price}
          <Typography component="span" variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            /month
          </Typography>
        </Typography>
        )}
       
        
     
     
        {finalPrice && finalPrice !== plan.price && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', textDecoration: 'line-through' }}>
              ₹{plan.price}/month
            </Typography>
            <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
              {Math.round((1 - finalPrice / plan.price) * 100)}% off
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderPlanCard = (plan) => {
    const isSelected = selectedPlan?.id === plan.id;
    const currentPrice = isSelected ? finalTotalAmount / plan.totalMonths : plan.price;
    const showDiscount = isSelected && finalTotalAmount / plan.totalMonths !== plan.price;

    return (
      <Grid item xs={12} md={8} lg={6} key={plan.id}>
        <PlanCard data-plan-id={plan.id}>
          {plan.badge && (
            <Box sx={{ position: 'relative', top: -15, textAlign: 'center' }}>
              <Chip
                label={plan.badge}
                color="success"
                sx={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  '& .MuiChip-label': {
                    px: 2
                  }
                }}
              />
            </Box>
          )}
          {/* Responsive layout with magazine cover */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row',
            gap: { xs: 2, sm: 4 }, 
            alignItems: 'flex-start', 
            mb: 3 
          }}>
            {/* Magazine Cover */}
            <Box sx={{ 
              flexShrink: 0,
              position: 'relative',
              alignSelf: { xs: 'center', sm: 'flex-start' }
            }}>
              <Box 
                onClick={() => handleImageClick("https://dp9h3awfnhccl.cloudfront.net/public_folder/issue_current.jpeg", "Current YoungPresso Issue")}
                sx={{
                  width: { xs: '90px', sm: '100px', md: '130px' },
                  height: { xs: '117px', sm: '130px', md: '170px' },
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                  border: '2px solid rgba(255, 107, 53, 0.3)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'scale(1.05) rotate(2deg)',
                    boxShadow: '0 12px 32px rgba(255, 107, 53, 0.4)',
                    border: '2px solid rgba(255, 107, 53, 0.6)'
                  }
                }}
              >
                <img
                  src="https://dp9h3awfnhccl.cloudfront.net/public_folder/issue_current.jpeg"
                  alt="Current YoungPresso Issue"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                  onError={(e) => {
                    e.target.style.backgroundColor = 'rgba(255, 107, 53, 0.1)';
                    e.target.alt = 'YoungPresso Magazine';
                  }}
                />
              </Box>
              
              {/* Floating badge for current issue */}
              <Box sx={{
                position: 'absolute',
                top: -8,
                right: -12,
                background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                color: 'white',
                padding: '3px 8px',
                borderRadius: '8px',
                fontSize: '0.65rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)',
                zIndex: 1,
                transform: 'rotate(-10deg)'
              }}>
                Latest
              </Box>
            </Box>

            {/* Plan Details */}
            <Box sx={{ 
              flex: 1, 
              minWidth: 0,
              width: '100%'
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                fontWeight: 'bold', 
                color: 'white',
                background: 'linear-gradient(45deg, #06AED5 30%, #4FC3F7 70%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                mb: 1,
                lineHeight: 1.2,
                textAlign: 'center'
              }}>
                {plan.name}
              </Typography>
              
              {/* Compact pricing display */}
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                {plan.period === 'yearly' && (
                  <Typography variant="h5" sx={{ 
                    fontSize: { xs: '1.6rem', sm: '2rem' }, 
                    fontWeight: 'bold', 
                    background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1
                  }}>
                    ₹{(currentPrice*12)}
                    <Typography component="span" variant="subtitle1" sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      ml: 0.5
                    }}>
                      /year
                    </Typography>
                  </Typography>
                )}
                
                {plan.period === 'monthly' && (
                  <Typography variant="h5" sx={{ 
                    fontSize: { xs: '1.6rem', sm: '2rem' }, 
                    fontWeight: 'bold', 
                    background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1
                  }}>
                    ₹{currentPrice}
                    <Typography component="span" variant="subtitle1" sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      ml: 0.5
                    }}>
                      /month
                    </Typography>
                  </Typography>
                )}
                
                {showDiscount && (
                  <Typography variant="body2" sx={{ 
                    color: '#4CAF50', 
                    fontWeight: 'bold',
                    fontSize: '0.8rem'
                  }}>
                    {Math.round((1 - currentPrice / plan.price) * 100)}% off
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <PlanFeatures>
            {plan.features.map((feature, index) => {
              const featureText = typeof feature === 'object' ? feature.text : feature;
              
              return (
                <FeatureItem key={index}>
                  <CheckCircleIcon />
                  <Typography dangerouslySetInnerHTML={{ __html: featureText }}></Typography>
                </FeatureItem>
              );
            })}
          </PlanFeatures>
          
          {/* Promotional Offer Banner - Set show={true} to display */}
          <OfferBanner 
            show={false}
            emoji="🎄"
            couponCode="SANTA2025"
            discountText="for 15% OFF"
            offerText="Limited Period Offer! 🎁"
          />
          
          <Button
            variant="contained"
            fullWidth
            sx={{
              background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
              color: 'white',
              fontWeight: 'bold',
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(45deg, #E85A2C 30%, #FF8A50 70%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(255, 107, 53, 0.4)'
              }
            }}
            onClick={() => handlePlanSelect(plan)}
          >
            Choose Plan
          </Button>

          {/* Importance of News Image */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 3,
            position: 'relative'
          }}>
            <img 
              src="https://dp9h3awfnhccl.cloudfront.net/public_folder/importance-of-news-home" 
              alt="Importance of News" 
              onClick={() => handleImageClick("https://dp9h3awfnhccl.cloudfront.net/public_folder/importance-of-news-home", "Importance of News")}
              style={{ 
                width: '100%', 
                maxWidth: '900px',
                height: 'auto', 
                borderRadius: '8px', 
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }} 
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.02)';
                e.target.style.boxShadow = '0 6px 20px rgba(255, 107, 53, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.3)';
              }}
            />
          </Box>
          
        </PlanCard>
      </Grid>
    );
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 30%, #2a2a2a 70%, #1a1a1a 100%)',
      minHeight: '100vh',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(255, 107, 53, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(6, 174, 213, 0.1) 0%, transparent 50%)',
        zIndex: -1
      }
    }}>
      <NavBar />
      
      {/* Pricing Banner */}
      <Box sx={{
        textAlign: 'center',
        py: { xs: 3, md: 4 },
        px: 2,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          display: 'inline-block',
          position: 'relative',
          px: { xs: 3, sm: 5, md: 6 },
          py: { xs: 2, sm: 2.5, md: 3 },
          background: 'linear-gradient(135deg, rgba(6, 174, 213, 0.15) 0%, rgba(255, 215, 0, 0.1) 50%, rgba(6, 174, 213, 0.15) 100%)',
          borderRadius: '20px',
          border: '2px solid rgba(6, 174, 213, 0.3)',
          boxShadow: '0 8px 32px rgba(6, 174, 213, 0.2), 0 0 0 1px rgba(255, 215, 0, 0.1)',
          backdropFilter: 'blur(10px)',
          animation: 'glow 2s ease-in-out infinite',
          '@keyframes glow': {
            '0%, 100%': {
              boxShadow: '0 8px 32px rgba(6, 174, 213, 0.2), 0 0 0 1px rgba(255, 215, 0, 0.1)',
              borderColor: 'rgba(6, 174, 213, 0.3)',
            },
            '50%': {
              boxShadow: '0 12px 48px rgba(6, 174, 213, 0.4), 0 0 0 2px rgba(255, 215, 0, 0.3), 0 0 30px rgba(255, 215, 0, 0.2)',
              borderColor: 'rgba(6, 174, 213, 0.5)',
            }
          },
          '&::before': {
            content: '"🎉"',
            position: 'absolute',
            left: { xs: '10px', sm: '15px' },
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: { xs: '1.5rem', sm: '2rem' },
            animation: 'bounce 1s ease-in-out infinite',
            '@keyframes bounce': {
              '0%, 100%': { transform: 'translateY(-50%) scale(1)' },
              '50%': { transform: 'translateY(-60%) scale(1.2)' }
            }
          },
          '&::after': {
            content: '"🎉"',
            position: 'absolute',
            right: { xs: '10px', sm: '15px' },
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: { xs: '1.5rem', sm: '2rem' },
            animation: 'bounce 1s ease-in-out infinite 0.5s',
          }
        }}>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.8rem' },
                fontWeight: 700,
                lineHeight: 1.2,
                color: '#06AED5',
                textShadow: '0 2px 10px rgba(6, 174, 213, 0.3)',
              }}
            >
              NEWS for Kids @ Just
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '2rem', sm: '2.6rem', md: '3.6rem' },
                fontWeight: 900,
                lineHeight: 1.2,
                color: '#FFD700',
                textShadow: '0 0 20px rgba(255, 215, 0, 0.5), 0 2px 10px rgba(255, 215, 0, 0.3)',
                animation: 'pulse 1.5s ease-in-out infinite',
                mt: 0.5,
              }}
            >
              ₹299/month
            </Typography>
          </Box>
        </Box>
        
        {/* View Plans Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={scrollToMonthlyPlan}
            sx={{
              background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
              color: 'white',
              fontWeight: 'bold',
              py: 1.5,
              px: 4,
              borderRadius: '12px',
              textTransform: 'none',
              fontSize: '1.1rem',
              boxShadow: '0 4px 20px rgba(255, 107, 53, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(45deg, #E85A2C 30%, #FF8A50 70%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(255, 107, 53, 0.5)'
              }
            }}
          >
            View Plans
          </Button>
        </Box>
      </Box>
      
      {/* YouTube Video Player */}
      {/* <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        justifyContent: 'center', 
        my: { xs: 4, md: 8 },
        position: 'relative',
        paddingTop: '56.25%'
      }}>
        <iframe
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '16px', 
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}
          src="https://www.youtube.com/embed/aYSOXjplpd4?autoplay=1&mute=1&modestbranding=1&rel=0"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        ></iframe>
      </Box> */}

      {/* Editions Info Section */}
      <EditionsInfo />
      
      {/* Testimonials Section */}
      <Testimonials />
      
      {/* Subscribe Now Message */}
      <Box sx={{ 
        textAlign: 'center', 
        py: 4,
        animation: 'pulse 2s ease-in-out infinite',
        '@keyframes pulse': {
          '0%, 100%': {
            transform: 'scale(1)',
          },
          '50%': {
            transform: 'scale(1.05)',
          }
        }
      }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 50%, #06AED5 70%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
            textShadow: '0 0 40px rgba(255, 107, 53, 0.3)',
          }}
        >
          Subscribe Now - Get this issue for FREE!
        </Typography>
        <ArrowDownwardIcon 
          sx={{ 
            fontSize: { xs: '2.5rem', sm: '3rem' },
            color: '#FF6B35',
            animation: 'bounce 2s ease-in-out infinite',
            filter: 'drop-shadow(0 0 10px rgba(255, 107, 53, 0.5))',
            '@keyframes bounce': {
              '0%, 100%': {
                transform: 'translateY(0)',
              },
              '50%': {
                transform: 'translateY(15px)',
              }
            }
          }} 
        />
      </Box>
      
      {/* Top Banner - Current Issue */}
      <Box sx={{ 
        position: 'relative',
        width: '100%',
        pt: 4,
        pb: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(5px)',
          borderRadius: '20px',
          zIndex: -1
        }
      }}>
        <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ 
            position: 'relative',
            display: 'inline-block',
            maxWidth: '400px',
            width: '100%'
          }}>
            <img 
              src="https://dp9h3awfnhccl.cloudfront.net/public_folder/issue_current.jpeg" 
              alt="YoungPresso Current Issue" 
              onClick={scrollToMonthlyPlan}
              style={{ 
                width: '100%', 
                height: 'auto', 
                borderRadius: '16px', 
                marginBottom: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                border: '2px solid rgba(255, 107, 53, 0.3)',
                transition: 'all 0.3s ease',
                display: 'block',
                cursor: 'pointer'
              }} 
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.02)';
                e.target.style.boxShadow = '0 12px 40px rgba(255, 107, 53, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4)';
              }}
            />
            
            {/* Overlapping Label on Top-Left Corner */}
            <Box sx={{ 
              position: 'absolute',
              top: { xs: '5px', sm: '10px' },
              left: { xs: '5px', sm: '10px' },
              zIndex: 10
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  display: 'inline-block',
                  fontWeight: 900,
                  color: '#FFFFFF',
                  fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' },
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  position: 'relative',
                  padding: { xs: '8px 16px', sm: '10px 20px' },
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { 
                      transform: 'scale(1) rotate(-5deg)',
                      filter: 'drop-shadow(0 0 10px rgba(255, 107, 53, 0.5))'
                    },
                    '50%': { 
                      transform: 'scale(1.05) rotate(-5deg)',
                      filter: 'drop-shadow(0 0 25px rgba(255, 107, 53, 0.8))'
                    }
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '110%',
                    height: '120%',
                    background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 215, 0, 0.8)',
                    backdropFilter: 'blur(10px)',
                    zIndex: -1,
                    boxShadow: '0 8px 32px rgba(255, 107, 53, 0.6), inset 0 0 20px rgba(255, 215, 0, 0.3)'
                  },
                  '&::after': {
                    content: '"✨"',
                    position: 'absolute',
                    right: '-15px',
                    top: '-10px',
                    fontSize: { xs: '1rem', sm: '1.2rem' },
                    animation: 'sparkle 2s ease-in-out infinite',
                    '@keyframes sparkle': {
                      '0%, 100%': { 
                        opacity: 0.3,
                        transform: 'rotate(0deg) scale(0.8)'
                      },
                      '50%': { 
                        opacity: 1,
                        transform: 'rotate(180deg) scale(1.2)'
                      }
                    }
                  }
                }}
              >
                🔥 Current Week Issue
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <PricingContainer maxWidth="xl" id="pricing-plans-section">
        <Grid container spacing={6} justifyContent="center" sx={{ px: { md: 4, lg: 8 } }}>
          {plans.map((plan) => renderPlanCard(plan))}
        </Grid>
      </PricingContainer>

      {/* FAQ Section */}
      <FAQ />

      
      {/* What is in the Magazine Section */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="h3"
          align="center"
          sx={{
            mb: 2,
            fontWeight: 900,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            background: 'linear-gradient(45deg, #FF6B35 30%, #06AED5 70%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          <span>What is </span>in the Magazine?
        </Typography>

        <Typography variant="h6" align="center" sx={{ color: 'rgba(255,255,255,0.8)', mb: 6, fontSize: '1.2rem' }}>
          Explore the exciting content our magazine offers for young minds
        </Typography>

        <Grid container spacing={4}>
          {[
            { 
              title: 'Current Affairs', 
              subtitle: 'Kid Friendly Language',
              icon: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/Breaking+News.png'
            },
            { 
              title: 'Tech Updates', 
              subtitle: 'Keep Kids aligned with latest Tech',
              icon: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/Tech+Updates.png'
            },
            { 
              title: 'Knowledge Bites', 
              subtitle: 'Word of the Week, Did You Know, Riddles etc.',
              icon: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/Did+You+Know.png'
            },
            { 
              title: 'Astronomy & Science', 
              subtitle: 'Space, Astronomy, Dinosaurs, Mummies, Cars and other Fascinating Topics',
              icon: 'https://dp9h3awfnhccl.cloudfront.net/public_folder/Wall+Of+Fame.png'
            }
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{
                height: '100%',
                p: 3,
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: '1px solid rgba(6,174,213,0.2)',
                transition: 'all 0.4s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 15px 35px rgba(6,174,213,0.3)',
                  border: '1px solid rgba(6,174,213,0.4)',
                }
              }}>
                <Box sx={{
                  width: '100%',
                  paddingTop: '100%',
                  position: 'relative',
                  mb: 3,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <img 
                      src={item.icon} 
                      alt={item.title}
                      style={{ 
                        width: '80%', 
                        height: '80%', 
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                </Box>
                <Typography 
                  variant="h6"
                  sx={{ 
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: { xs: '1.25rem', md: '1.4rem' },
                    mb: 1,
                    textAlign: 'center'
                  }}
                >
                  {item.title}
                </Typography>
                <Typography 
                  variant="subtitle1"
                  sx={{ 
                    color: '#06AED5',
                    fontSize: { xs: '0.95rem', md: '1rem' },
                    fontWeight: 500,
                    textAlign: 'center'
                  }}
                >
                  {item.subtitle}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ 
        pt: 4, 
        pb: 2, 
        textAlign: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(5px)',
          borderRadius: '20px',
          zIndex: -1
        }
      }}>
        <Container maxWidth="xl">
          <img 
            src="https://dp9h3awfnhccl.cloudfront.net/banner.jpg" 
            alt="YoungPresso Banner" 
            onClick={() => handleImageClick("https://dp9h3awfnhccl.cloudfront.net/banner.jpg", "YoungPresso Banner")}
            style={{ 
              width: '100%', 
              maxWidth: '600px',
              height: 'auto', 
              borderRadius: '12px', 
              marginBottom: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }} 
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.02)';
              e.target.style.boxShadow = '0 12px 40px rgba(255, 107, 53, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4)';
            }}
          />
        </Container>
      </Box>
      
      {/* Magazine Covers Section */}
      <Container maxWidth="xl" sx={{ 
        py: 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.02)',
          backdropFilter: 'blur(5px)',
          borderRadius: '20px',
          zIndex: -1
        }
      }}>
        <MagazineCoverGrid 
          onClick={() => {
            // Find the monthly plan element and scroll to it
            const pricingSection = document.querySelector('[data-plan-id="xx1"]');
            if (pricingSection) {
              pricingSection.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
            } else {
              // Fallback: scroll to pricing container
              const container = document.querySelector('.MuiContainer-root');
              if (container) {
                container.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'center' 
                });
              }
            }
          }}
        />
      </Container>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/terms-and-conditions" style={{ color: '#FF6B35', textDecoration: 'none' }}>Terms & Conditions</Link>
            <Link to="/cancellation-refund-policy" style={{ color: '#FF6B35', textDecoration: 'none' }}>Cancellation & Refund Policy</Link>
          </Typography>
        </Box>
      
      <PaymentDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedPlan(null);
          setError(null);
          setFormData({
            name: '',
            email: '',
            phone: '',
            couponCode: '',
            childAge: '',
            childGender: '',
            interests: {}
          });
          setCouponStatus({
            isValid: false,
            message: '',
            discount: null
          });
        }}
        plan={selectedPlan}
        formData={formData}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        couponStatus={couponStatus}
        validateCoupon={validateCoupon}
        finalTotalAmount={finalTotalAmount}
        isCreatingSubscription={isCreatingSubscription}
        isValidatingCoupon={isValidatingCoupon}
        setCouponStatus={setCouponStatus}
        setFinalTotalAmount={setFinalTotalAmount}
      />

      {/* Full-screen Image Modal */}
      <ImageModal
        open={imageModalOpen}
        onClose={handleImageModalClose}
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
        }}
      >
        <ModalContent>
          <CloseButton onClick={handleImageModalClose}>
            <CloseIcon />
          </CloseButton>
          <ModalImage
            src={selectedImage.url}
            alt={selectedImage.alt}
            onError={(e) => {
              e.target.style.backgroundColor = '#f5f5f5';
              e.target.alt = 'Image not available';
            }}
          />
        </ModalContent>
      </ImageModal>

      <Footer />
    </Box>
  );
};

export default PricingAndPlans;
